import {initializeApp} from "firebase/app";
import {
    AuthErrorCodes,
    EmailAuthProvider,
    FacebookAuthProvider,
    getAuth,
    GoogleAuthProvider,
    sendEmailVerification,
    sendPasswordResetEmail,
    signInWithCredential,
    signInWithPopup,
    updateProfile
} from "firebase/auth";

export default {
    data() {
        return {
            authenticator: null,
            localUser: null,
            reCaptchaContainer: null,
            isSigningIn: false
        };
    },

    mounted() {
        this.authenticator = getAuth(initializeApp({
            apiKey: document.querySelector('meta[name="fb-key"]').content,
            authDomain: document.querySelector('meta[name="fb-domain"]').content,
            projectId: document.querySelector('meta[name="fb-project"]').content,
        }));
    },

    methods: {
        getTermsOfServiceUrl() {
            return this.$frontendUrl() + '/agb';
        },

        getPrivacyPolicyUrl() {
            return this.$frontendUrl() + '/datenschutz';
        },

        setReCaptchaContainer(ref) {
            this.reCaptchaContainer = ref;
        },

        attemptSignInWithCredentials(email, password, showMfaInputFunction, successFunction = null, errorFunction = null) {
            this.isSigningIn = true;
            const credentials = EmailAuthProvider.credential(email, password);
            this.attemptSignIn(signInWithCredential(this.authenticator, credentials), showMfaInputFunction, successFunction, errorFunction);
        },

        attemptSignInWithGoogle(showMfaInputFunction, successFunction = null, errorFunction = null) {
            this.isSigningIn = true;
            this.attemptSignIn(signInWithPopup(this.authenticator, new GoogleAuthProvider()), showMfaInputFunction, successFunction, errorFunction);
        },

        attemptSignInWithFacebook(showMfaInputFunction, successFunction = null, errorFunction = null) {
            this.isSigningIn = true;
            this.attemptSignIn(signInWithPopup(this.authenticator, new FacebookAuthProvider()), showMfaInputFunction, successFunction, errorFunction);
        },

        attemptSignInWithApple(showMfaInputFunction, successFunction = null, errorFunction = null) {
            throw new Error("Apple auth provider not implemented.");
            // this.isSigningIn = true;
            // this.attemptSignIn(signInWithPopup(this.authenticator, new AppleAuthProvider()), showMfaInputFunction, successFunction, errorFunction);
        },

        attemptSignIn(signInPromise, showMfaInputFunction, successFunction = null, errorFunction = null) {
            signInPromise
                .then((result) => {
                    this.isSigningIn = false;
                    this.localUser = result.user;

                    result.user.getIdToken()
                        .then((userIdToken) => {
                            console.info('[Auth] User authenticated.');
                            if (!this.isUserEmailVerified(result.user)) {
                                console.info('[Auth] Users email is not verified.');
                            }
                            if (successFunction !== null && successFunction !== undefined) {
                                successFunction(result.user, userIdToken);
                            }
                        });
                })
                .catch((error) => {
                    this.isSigningIn = false;
                    if (errorFunction !== null) {
                        errorFunction(error);
                    }
                });
        },

        isUserEmailVerified(user) {
            return user.emailVerified;
        },

        attemptDashboardSignIn(idToken, signInFailedHandler) {
            axios.post(this.$route('login.verify'), {id_token: idToken})
                .then(response => {
                    if (response.status === 200) {
                        const urlParams = new URLSearchParams(window.location.search);

                        let target = this.$route('dashboard').toString();
                        let encodedReturnUrl = urlParams.get('to');

                        if (encodedReturnUrl) {
                            target = this.$route('login.redirect', [encodedReturnUrl]).toString();
                        }

                        console.info('[Auth] Redirecting: ' + target);
                        window.location = target;
                    } else {
                        console.error('[Auth] Verification failed.');
                        signInFailedHandler();
                    }
                })
                .catch(signInFailedHandler);
        },

        updateUserDisplayName(user, displayName) {
            if (displayName.trim().length === 0) {
                throw new Error("[Auth] Display name can't be empty.");
            }

            updateProfile(user, {
                displayName: displayName
            }).then(() => {
                console.info('[Auth] Display name updated.');
            }).catch((error) => {
                console.error('[Auth] Failed to update display name.');
            });
        },

        requestPasswordReset(email, successHandler, errorHandler) {
            sendPasswordResetEmail(this.authenticator, email)
                .then(successHandler)
                .catch(errorHandler);
        },

        sendVerificationEmail(user, successHandler, errorHandler) {
            if (user === null) {
                console.error('[Auth] User is not authenticated.');
                return;
            }

            sendEmailVerification(user)
                .then(successHandler)
                .catch(errorHandler);
        },

        mapAuthError(error) {
            switch (error.code) {
                case AuthErrorCodes.INVALID_CODE:
                    return {
                        title: __('general.thing.is_invalid', {thing: __('auth.mfa_code')}),
                        message: __('auth.please_check_input_and_try_again')
                    };

                case AuthErrorCodes.USER_DISABLED:
                    return {
                        title: __('auth.account_disabled'),
                        message: __('auth.contact_support_to_reenable')
                    };

                case AuthErrorCodes.USER_DELETED:
                case AuthErrorCodes.INVALID_PASSWORD:
                    return {
                        title: __('auth.invalid_credentials'),
                        message: __('auth.please_check_input_and_try_again')
                    };

                case 'auth/missing-password':
                    return {
                        title: __('general.thing.is_missing', {thing: __('user.password')}),
                        message: __('auth.please_check_input_and_try_again')
                    };

                case AuthErrorCodes.CREDENTIAL_ALREADY_IN_USE:
                case AuthErrorCodes.EMAIL_EXISTS:
                    return {
                        title: __('general.thing.already_exists', {thing: __('user.account')}),
                        message: __('auth.please_check_input_and_try_again')
                    };

                case AuthErrorCodes.INVALID_PHONE_NUMBER:
                    return {
                        title: __('general.thing.is_invalid', {thing: __('user.mobile_number')}),
                        message: __('auth.please_check_input_and_try_again')
                    };

                case 'auth/missing-email':
                case AuthErrorCodes.INVALID_EMAIL:
                    return {
                        title: __('general.thing.is_invalid', {thing: __('user.email')}),
                        message: __('auth.please_check_input_and_try_again')
                    };

                case AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER:
                    return {
                        title: __('auth.too_many_attempts'),
                        message: __('auth.please_try_again_later')
                    };

                default:
                    return {
                        title: __('general.unknown_error'),
                        message: __('auth.please_try_again_later')
                    };
            }
        }
    },

    computed: {
        authenticatorIsInitialized() {
            return this.authenticator !== null;
        },

        userIsAuthenticated() {
            return this.localUser !== null;
        }
    }
};
